import { useCallback, useEffect, useRef } from 'react';
import type { Awareness } from 'y-protocols/awareness';
import type { SharedInFlightEdge } from '../../../engine/render/awareness-types';

const CURSOR_THROTTLE_MS = 33; // ~30 Hz

export interface UseCursorAwarenessArgs {
  awareness: Awareness;
}

export interface UseCursorAwarenessResult {
  reportPointer: (point: { x: number; y: number } | null) => void;
  reportSelection: (ids: string[]) => void;
  reportEdgeSelection: (ids: string[]) => void;
  /** Throttled write of in-flight edge drag state. Pass null to clear on dragEnd. */
  reportInFlightEdge: (edge: SharedInFlightEdge | null) => void;
}

export function useCursorAwareness({
  awareness,
}: UseCursorAwarenessArgs): UseCursorAwarenessResult {
  const pendingRef = useRef<{ x: number; y: number } | null | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    timerRef.current = null;
    if (pendingRef.current === undefined) return;
    const next = pendingRef.current;
    pendingRef.current = undefined;
    const existing = awareness.getLocalState() ?? {};
    awareness.setLocalState({ ...existing, canvasCursor: next });
  }, [awareness]);

  const reportPointer = useCallback(
    (point: { x: number; y: number } | null) => {
      pendingRef.current = point;
      if (timerRef.current !== null) return;
      // leading-edge: fire immediately, then schedule trailing-edge for any updates within the window
      flush();
      timerRef.current = setTimeout(flush, CURSOR_THROTTLE_MS);
    },
    [flush],
  );

  const reportSelection = useCallback(
    (ids: string[]) => {
      const existing = awareness.getLocalState() ?? {};
      awareness.setLocalState({ ...existing, canvasSelection: ids });
    },
    [awareness],
  );

  const reportEdgeSelection = useCallback(
    (ids: string[]) => {
      const existing = awareness.getLocalState() ?? {};
      awareness.setLocalState({ ...existing, canvasEdgeSelection: ids });
    },
    [awareness],
  );

  // Throttle in-flight edge writes to the same 30Hz cadence as cursor updates.
  // The drag fires onConnection at the browser's pointermove rate (~120Hz on
  // trackpads); without throttling we'd flood awareness with redundant frames.
  // Leading-edge fire mirrors reportPointer so the start of a drag is
  // immediate; the trailing flush catches the final position.
  const inFlightPendingRef = useRef<SharedInFlightEdge | null | undefined>(undefined);
  const inFlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushInFlight = useCallback(() => {
    inFlightTimerRef.current = null;
    if (inFlightPendingRef.current === undefined) return;
    const next = inFlightPendingRef.current;
    inFlightPendingRef.current = undefined;
    const existing = awareness.getLocalState() ?? {};
    awareness.setLocalState({ ...existing, canvasInFlightEdge: next });
  }, [awareness]);

  const reportInFlightEdge = useCallback(
    (edge: SharedInFlightEdge | null) => {
      inFlightPendingRef.current = edge;
      // null (drag end) flushes immediately so peers see the ghost disappear without a 33ms tail.
      if (edge === null) {
        if (inFlightTimerRef.current) {
          clearTimeout(inFlightTimerRef.current);
          inFlightTimerRef.current = null;
        }
        flushInFlight();
        return;
      }
      if (inFlightTimerRef.current !== null) return;
      flushInFlight();
      inFlightTimerRef.current = setTimeout(flushInFlight, CURSOR_THROTTLE_MS);
    },
    [flushInFlight],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (inFlightTimerRef.current) clearTimeout(inFlightTimerRef.current);
    };
  }, []);

  return { reportPointer, reportSelection, reportEdgeSelection, reportInFlightEdge };
}
